import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Clock, MapPin, Receipt, Printer } from 'lucide-react';
import Header from '../components/Header';
import { useApp } from '../context/AppContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';

const statusBadge = (status) => {
  const map = {
    placed: { label: 'Placed', cls: 'bg-slate-100 text-slate-700' },
    confirmed: { label: 'Confirmed', cls: 'bg-blue-50 text-blue-700' },
    preparing: { label: 'Preparing', cls: 'bg-amber-50 text-amber-700' },
    on_the_way: { label: 'On the way', cls: 'bg-emerald-50 text-emerald-700' },
    delivered: { label: 'Delivered', cls: 'bg-slate-100 text-slate-600' },
  };
  return map[status] || map.placed;
};

const Orders = () => {
  const { orders, format } = useApp();
  const nav = useNavigate();

  return (
    <div className="min-h-screen bg-slate-50">
      <Header showSearch={false} />
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Your orders</h1>
        <p className="text-slate-500 mt-1">Track ongoing deliveries and revisit past orders.</p>

        {orders.length === 0 ? (
          <div className="mt-8 bg-white border border-slate-200 rounded-2xl p-10 text-center">
            <div className="h-14 w-14 rounded-full bg-slate-100 mx-auto flex items-center justify-center">
              <Receipt className="h-6 w-6 text-slate-400" />
            </div>
            <h3 className="mt-4 font-semibold text-slate-900">No orders yet</h3>
            <Link to="/"><Button className="mt-4 bg-emerald-600 hover:bg-emerald-700">Start ordering</Button></Link>
          </div>
        ) : (
          <div className="mt-6 space-y-4">
            {orders.map((o) => {
              const sb = statusBadge(o.status);
              const active = o.status !== 'delivered';
              return (
                <div
                  key={o.id}
                  className="w-full bg-white border border-slate-200 rounded-2xl p-5 hover:border-slate-300 card-hover"
                >
                  <div className="flex items-start justify-between gap-4">
                    <button onClick={() => nav(`/track/${o.id}`)} className="flex-1 min-w-0 text-left" data-testid={`order-row-${o.id}`}>
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-slate-900 truncate">
                          {(o.vendor_system || String(o.vendorId || o.vendor_id || '').startsWith('sys-')) ? 'ShipitNow delivery' : (o.vendorName || o.vendor_name)}
                        </h3>
                        <Badge className={`${sb.cls} hover:${sb.cls} border-0`}>{sb.label}</Badge>
                      </div>
                      <div className="mt-1.5 text-sm text-slate-500">{o.items.length} item{o.items.length !== 1 ? 's' : ''} • Order {o.id}</div>
                      <div className="mt-2 flex items-center flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500">
                        {active && o.eta && <span className="inline-flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> {o.eta}</span>}
                        <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {o.address}</span>
                      </div>
                    </button>
                    <div className="text-right shrink-0">
                      <div className="font-semibold text-slate-900">{format(o.total, o.currency)}</div>
                      <div className="text-xs text-slate-500 mt-0.5">{new Date(o.placedAt).toLocaleDateString()}</div>
                      <Link
                        to={`/orders/${o.id}/invoice`}
                        className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-slate-700 hover:text-slate-900 border border-slate-200 hover:border-slate-300 rounded-full px-2.5 py-1"
                        data-testid={`view-invoice-${o.id}`}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Printer className="h-3 w-3" /> Invoice
                      </Link>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
};

export default Orders;
